import { usePagination } from "../../shared/lib/use-pagination.js";
import { Pagination } from "../../shared/ui/pagination.js";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAccounts } from "../settings/queries.js";
import { RefreshButton } from "../../shared/ui/refresh-button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { FilteredBacktestLauncher } from "./filtered-backtest-launcher.js";
import { FilteredLiveControls } from "./filtered-live-controls.js";
import { FilteredSizingRiskControls } from "./filtered-sizing-risk-controls.js";
import { botStates, type BotState } from "./models.js";
import { useBots } from "./queries.js";

type Sort = "name" | "symbol" | "updated";

function isBotState(value: string): value is BotState {
  return botStates.some((state) => state === value);
}

export function BotListPage() {
  const [search, setSearch] = useSearchParams();
  const stateValue = search.get("state") ?? "";
  const symbolValue = search.get("symbol") ?? "";
  const [nameFilter, setNameFilter] = useState(search.get("name") ?? "");
  const sort = (search.get("sort") ?? "updated") as Sort;
  const bots = useBots({
    ...(isBotState(stateValue) ? { state: stateValue } : {}),
    ...(symbolValue === "" ? {} : { symbol: symbolValue.toUpperCase() }),
  });
  const accounts = useAccounts();
  const accountNameById = new Map(
    (accounts.data ?? []).map((account) => [account.id, account.displayName]),
  );
  const visible = useMemo(() => {
    const result = (bots.data ?? []).filter((bot) =>
      bot.name.toLocaleLowerCase().includes(nameFilter.toLocaleLowerCase()),
    );
    return [...result].sort((left, right) => {
      if (sort === "name") return left.name.localeCompare(right.name);
      if (sort === "symbol") return left.symbol.localeCompare(right.symbol);
      return right.updatedAt.localeCompare(left.updatedAt);
    });
  }, [bots.data, nameFilter, sort]);

  const pagination = usePagination(visible);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setSearch(next, { replace: true });
  };

  return (
    <section className="page-stack page-stack--compact" aria-label="Bots">
      <div className="page-toolbar">
        <h1>Bots</h1>
        <RefreshButton queries={[bots, accounts]} />
      </div>
      <div className="content-card bot-controls">
        <div className="compact-actions">
          <FilteredLiveControls bots={visible} />
          <FilteredSizingRiskControls bots={visible} />
          <FilteredBacktestLauncher bots={visible} />
          <Link className="button button--primary" to="/bots/new">
            Create bot
          </Link>
        </div>
        <div className="filter-bar" aria-label="Bot filters">
          <div className="field-stack">
            <label htmlFor="bot-name-filter">Search name</label>
            <input
              id="bot-name-filter"
              value={nameFilter}
              onChange={(event) => {
                setNameFilter(event.currentTarget.value);
                setFilter("name", event.currentTarget.value);
              }}
            />
          </div>
          <div className="field-stack">
            <label htmlFor="bot-symbol-filter">Symbol</label>
            <input
              id="bot-symbol-filter"
              value={symbolValue}
              onChange={(event) => setFilter("symbol", event.currentTarget.value)}
            />
          </div>
          <div className="field-stack">
            <label htmlFor="bot-state-filter">Lifecycle state</label>
            <select
              id="bot-state-filter"
              value={stateValue}
              onChange={(event) => setFilter("state", event.currentTarget.value)}
            >
              <option value="">All states</option>
              {botStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
          <div className="field-stack">
            <label htmlFor="bot-sort">Sort</label>
            <select
              id="bot-sort"
              value={sort}
              onChange={(event) => setFilter("sort", event.currentTarget.value)}
            >
              <option value="updated">Recently updated</option>
              <option value="name">Name</option>
              <option value="symbol">Symbol</option>
            </select>
          </div>
        </div>
      </div>
      {bots.isLoading ? (
        <ResourceState
          kind="loading"
          title="Loading bots"
          description="Reading your Bot inventory from TradingEngine."
        />
      ) : null}
      {bots.error !== null ? (
        <ResourceState
          kind="error"
          title="Bots unavailable"
          description="TradingEngine could not return your Bots. Retry when the service is available."
        />
      ) : null}
      {!bots.isLoading && bots.error === null && visible.length === 0 ? (
        <ResourceState
          kind="empty"
          title="No matching bots"
          description="Create a Bot or change the current filters."
          action={
            <Link className="button button--primary" to="/bots/new">
              Create bot
            </Link>
          }
        />
      ) : null}
      {visible.length === 0 ? null : (
        <div className="bot-grid">
          {pagination.items.map((bot) => (
            <Link className="bot-card" key={bot.id} to={`/bots/${bot.id}`}>
              <div className="bot-card__heading">
                <div>
                  <span className="symbol-mark">{bot.symbol}</span>
                  <h2>{bot.name}</h2>
                </div>
                <span className={`status-pill status-pill--${bot.state}`}>{bot.state}</span>
              </div>
              <dl>
                <div>
                  <dt>Revision</dt>
                  <dd>
                    {bot.activeRevisionId === "" ? "Unavailable" : bot.activeRevisionId.slice(0, 8)}
                  </dd>
                </div>
                <div>
                  <dt>Live account</dt>
                  <dd>
                    {bot.liveBrokerAccountId === undefined
                      ? "Not assigned"
                      : (accountNameById.get(bot.liveBrokerAccountId) ??
                        (accounts.isLoading ? "Loading account…" : "Account unavailable"))}
                  </dd>
                </div>
                <div>
                  <dt>Updated</dt>
                  <dd>
                    {bot.updatedAt === "" ? "Unknown" : new Date(bot.updatedAt).toLocaleString()}
                  </dd>
                </div>
              </dl>
            </Link>
          ))}
        </div>
      )}
      <Pagination {...pagination} disabled={bots.isFetching || bots.error !== null} />
    </section>
  );
}
